import { Injectable } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Subject } from 'rxjs';
import { HIDEABLE_BOOKING_STEPS } from '@constants/app.constants';
import { BOOKING_WIDGET_LOCAL_STORAGE_CONSTANTS } from '@constants/local-storage.constants';
import { AppointmentServiceModel } from 'src/app/models/appointment-service.model';
import { CustomerDbModel } from '../../models/customer.model';
import { CustomFieldModel } from '../../models/custom-field.model';
import { WidgetConfModel, WidgetContext } from '../../models/widget-conf.model';
import { CustomEventService } from '../feature/custom-event.service';
import { WidgetService } from '../feature/widget.service';
import { DomManipulationService } from './dom-manipulation.service';
import { HelperService } from './helper.service';
import { LocalstorageService } from './localstorage.service';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class SwRouteService {

  private queryParamsData: {
    widgetContext: WidgetContext,
    customerConfData: CustomerDbModel,
    customFieldValues: { id?: number, value?: any, ignore?: boolean, rawKey?: string }[],
    referenceAppointment?: {
      uuid: string,
      appointment: AppointmentServiceModel
    }
  } = {
      widgetContext: new WidgetContext(),
      customerConfData: new CustomerDbModel(),
      customFieldValues: [],
      referenceAppointment: {
        uuid: null,
        appointment: null
      }
    };

  // ============================================
  // STRIPE REDIRECT PAYMENT HANDLING
  // All Stripe redirect logic is centralized here
  // ============================================

  /**
   * Parent page URL received via postMessage (for cross-origin iframe scenarios).
   * Used for Stripe redirect payments where parent page receives the redirect.
   */
  private parentUrl: string = '';

  /**
   * Stripe redirect data extracted from URL query parameters after 3DS/SCA verification.
   */
  private stripeRedirectData: {
    paymentIntent: string | null,
    paymentIntentClientSecret: string | null
  } = {
      paymentIntent: null,
      paymentIntentClientSecret: null
    };

  /**
   * Emits when Stripe redirect is detected from parent URL (late detection).
   * Used when parent URL arrives after initial isStripeRedirect check in booking.component.
   */
  stripeRedirectDetected$ = new Subject<void>();

  /**
   * `refresh` was PRESENT on the URL / attributes, whatever its value — `?refresh=0` and a bare
   * `?refresh` must both veto the context clear, and coercion cannot tell those from absent.
   * Same presence-not-truthiness shape as the `auto_skip_steps !== undefined` override below.
   */
  private refreshQueryParamPresent = false;

  /** Guards applyContextRefresh() to once per page load. */
  private contextRefreshApplied = false;

  get redirectedPaymentIntent(): string | null {
    return this.stripeRedirectData.paymentIntent;
  }

  get redirectedPaymentIntentClientSecret(): string | null {
    return this.stripeRedirectData.paymentIntentClientSecret;
  }

  /**
   * Check if this is a Stripe redirect return.
   */
  get isStripeRedirect(): boolean {
    return !!(this.stripeRedirectData.paymentIntent && this.stripeRedirectData.paymentIntentClientSecret);
  }

  /**
   * Initialize parent URL detection on app startup.
   * For cross-origin iframes, requests parent URL via postMessage.
   * Parent page responds with PARENT_URL_RESPONSE containing its URL.
   */
  initParentUrl(): void {
    const isInIframe = window !== window.parent;

    if (isInIframe) {
      window.addEventListener('message', (event) => {
        if (event?.data?.type === 'PARENT_URL_RESPONSE' && event?.data?.parentUrl) {
          this.parentUrl = event.data.parentUrl;
          LoggerService.log('[Stripe Redirect] Received parent URL via postMessage:', this.parentUrl);

          // Track if we already had Stripe params before
          const hadParamsBefore = this.isStripeRedirect;

          // Extract Stripe redirect parameters from parent URL
          this.extractStripeParamsFromParentUrl(this.parentUrl);

          // Emit event if Stripe redirect was detected (and wasn't detected before)
          // This handles the race condition where booking.component checks isStripeRedirect
          // before parent URL arrives
          if (!hadParamsBefore && this.isStripeRedirect) {
            LoggerService.log('[Stripe Redirect] Late detection - emitting stripeRedirectDetected$');
            this.stripeRedirectDetected$.next();
          }
        }
      });

      try {
        window.parent.postMessage({ eventName: 'REQUEST_PARENT_URL' }, '*');
        LoggerService.log('[Stripe Redirect] Requested parent URL via postMessage');
      } catch (error) {
        LoggerService.warn('[Stripe Redirect] Failed to request parent URL:', error);
      }
    }
  }

  /**
   * Extract Stripe redirect parameters from parent URL string.
   */
  private extractStripeParamsFromParentUrl(url: string): void {
    try {
      const urlObj = new URL(url);
      const paymentIntent = urlObj.searchParams.get('payment_intent');
      const clientSecret = urlObj.searchParams.get('payment_intent_client_secret');

      if (paymentIntent && clientSecret) {
        LoggerService.log('[Stripe Redirect] Detected redirect from Stripe (parent URL params)');
        this.stripeRedirectData.paymentIntent = paymentIntent;
        this.stripeRedirectData.paymentIntentClientSecret = clientSecret;
      }
    } catch (error) {
      LoggerService.warn('[Stripe Redirect] Failed to parse parent URL:', error);
    }
  }

  /**
   * Get the return URL for Stripe payments with fallback chain:
   * 1. parentUrl from postMessage (for cross-origin iframe scenarios)
   * 2. window.location.href (for direct iframe embed scenarios)
   *
   * IMPORTANT: Cleans Stripe redirect params to prevent duplicate params
   * when Stripe appends its own params on redirect.
   */
  getStripeReturnUrl(): string {
    let url: string;

    // Priority 1: Parent URL from postMessage
    if (this.parentUrl) {
      url = this.parentUrl;
    } else {
      // Priority 2: Current window location (fallback)
      url = window.location.href;
    }

    // Clean Stripe redirect params to prevent duplicates
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.delete('payment_intent');
      urlObj.searchParams.delete('payment_intent_client_secret');
      urlObj.searchParams.delete('redirect_status');
      url = urlObj.toString();
    } catch (e) {
      LoggerService.warn('[Stripe Redirect] Failed to clean URL:', e);
    }

    LoggerService.log('[Stripe Redirect] Return URL:', url);
    return url;
  }

  /**
   * Get stored booking data for Stripe redirect restoration.
   */
  getStripeRedirectionData(): Record<string, unknown> | null {
    const data = this.localStorageService.getItem(BOOKING_WIDGET_LOCAL_STORAGE_CONSTANTS.BOOKING_STRIPE_REDIRECTION_DATA);
    return data ? JSON.parse(data) : null;
  }

  /**
   * Clear Stripe redirection data from localStorage after successful booking.
   */
  clearStripeRedirectionData(): void {
    this.localStorageService.removeItem(BOOKING_WIDGET_LOCAL_STORAGE_CONSTANTS.BOOKING_STRIPE_REDIRECTION_DATA);
    this.stripeRedirectData.paymentIntent = null;
    this.stripeRedirectData.paymentIntentClientSecret = null;
    this.stripeRedirectionData = null;
  }

  set setReferenceAppointment(appointment: AppointmentServiceModel) {
    this.queryParamsData.referenceAppointment.appointment = appointment;
  }
  get getReferenceAppointment(): AppointmentServiceModel {
    return this.queryParamsData.referenceAppointment.appointment;
  }

  set setReferenceAppointmentUuid(uuid: string) {
    this.queryParamsData.referenceAppointment.uuid = uuid;
  }
  get getReferenceAppointmentUuid(): string {
    return this.queryParamsData.referenceAppointment.uuid;
  }

  get getReferenceAppointmentId(): number {
    const appointment = this.getReferenceAppointment;
    return appointment?.id ? appointment.id : undefined;
  }

  get zipCodeFromQueryParamsData(): string {
    return this.queryParamsData.widgetContext?.store_zip;
  }

  get storeIdFromQueryParamsData(): number {
    return this.queryParamsData.widgetContext?.store_id;
  }

  stripeRedirectionData: Record<string, any>;

  constructor(
    private route: ActivatedRoute,
    public widgetService: WidgetService,
    private customEventService: CustomEventService,
    private helperService: HelperService,
    private localStorageService: LocalstorageService,
    private domManuplationService: DomManipulationService
  ) {

    this.route.queryParams.subscribe(
      (result: Params) => {
        // In WC mode the element reads config from HTML attributes, never from the host URL.
        if (this.widgetService.isWebComponentMode) { return; }

        // Extract Stripe redirect parameters if present in the query params
        this.extractStripeRedirectParams(result);

        if (result.partner_uuid && result.widget_uuid) {
          this.customEventService.setWidgetConfigurationFromQueryParamsEvent.emit({
            partner_uuid: result.partner_uuid,
            widget_uuid: result.widget_uuid,
          });
        }
        this.stripeRedirectionData = this.getStripeRedirectionData();
        this.refreshQueryParamPresent = result.refresh !== undefined;
        if (Number(result.refresh) === 1) {
          this.localStorageService.clearWidgetKeys();
        }

        if (result?.appointment_uuid) {
          this.validateReferenceAppointmentAndSetInContext(result.appointment_uuid);
        }

        this.setCustomerConfData(result);
        this.setWidgetContextData(result);
        this.setCustomFieldValues(result);
      }
    );
  }

  /**
   * Extract Stripe redirect parameters from URL and clean up the URL.
   * Called after 3DS/SCA verification when Stripe redirects back to the iframe.
   *
   * Priority 1: Check iframe's own URL params (direct embed scenario)
   * Priority 2: Check parent URL received via postMessage (cross-origin iframe scenario)
   */
  private extractStripeRedirectParams(params: Params): void {
    // Priority 1: Check params from iframe's URL query string
    if (params.payment_intent && params.payment_intent_client_secret) {
      LoggerService.log('[Stripe Redirect] Detected redirect from Stripe (iframe URL params)');
      this.stripeRedirectData.paymentIntent = params.payment_intent;
      this.stripeRedirectData.paymentIntentClientSecret = params.payment_intent_client_secret;
      this.cleanStripeParamsFromUrl();
      return;
    }

    // Priority 2: Check parent URL (for cross-origin iframe scenarios)
    // Parent page receives Stripe redirect params and passes them via postMessage
    if (this.parentUrl) {
      try {
        const parentUrlObj = new URL(this.parentUrl);
        const paymentIntent = parentUrlObj.searchParams.get('payment_intent');
        const clientSecret = parentUrlObj.searchParams.get('payment_intent_client_secret');

        if (paymentIntent && clientSecret) {
          LoggerService.log('[Stripe Redirect] Detected redirect from Stripe (parent URL params)');
          this.stripeRedirectData.paymentIntent = paymentIntent;
          this.stripeRedirectData.paymentIntentClientSecret = clientSecret;
        }
      } catch (error) {
        LoggerService.warn('[Stripe Redirect] Failed to parse parent URL:', error);
      }
    }
  }

  /**
   * Remove Stripe redirect parameters from the browser URL without page reload.
   */
  private cleanStripeParamsFromUrl(): void {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('payment_intent');
      url.searchParams.delete('payment_intent_client_secret');
      url.searchParams.delete('redirect_status');
      // Preserve history.state — in WC mode this is the HOST SPA's routing state.
      window.history.replaceState(window.history.state, '', url.toString());
      LoggerService.log('[Stripe Redirect] Cleaned Stripe parameters from URL');
    } catch (error) {
      LoggerService.error('[Stripe Redirect] Failed to clean URL parameters: ' + String(error));
    }
  }

  changeCustomFieldStateToIgnore(id: number): void {
    if (this.queryParamsData.customFieldValues?.length > 0) {
      LoggerService.log('this.queryParamsData.customFieldValues ', this.queryParamsData.customFieldValues);
      for (const cf of this.queryParamsData.customFieldValues) {
        if (cf.id === id) {
          cf.ignore = true;
        }
      }
    }
  }

  getCustomFieldValues(): { id?: number, value?: any, ignore?: boolean, rawKey?: string }[] {
    return this.queryParamsData.customFieldValues;
  }

  /**
   * Translates identifier-keyed prefill entries to their numeric custom-field id, using the
   * partner's loaded booking questions. Preserves the raw URL key so this is safe to re-run
   * with a partial question list: a raw key that matches a question's `identifier` wins and
   * maps to that question's id; a genuinely numeric raw key falls back to its legacy id; any
   * other unmatched key leaves the entry's prior id untouched (never clobbered to NaN), so a
   * later call with a different list cannot undo an already-resolved entry.
   *
   * @param customFields the partner's loaded booking questions for the current flow
   */
  resolveCustomFieldIdentifiers(customFields: CustomFieldModel[]): void {
    const list = this.queryParamsData?.customFieldValues ?? [];
    const idByIdentifier = new Map<string, number>();
    for (const cf of customFields ?? []) {
      if (cf?.identifier) {
        idByIdentifier.set(cf.identifier, cf.id);
      }
    }
    for (const entry of list) {
      const mapped = entry.rawKey != null ? idByIdentifier.get(entry.rawKey) : undefined;
      if (mapped !== undefined) {
        entry.id = mapped;                        // identifier match wins
      } else {
        const n = Number(entry.rawKey);
        if (!Number.isNaN(n)) {
          entry.id = n;                           // legacy numeric id; leave prior id otherwise
        }
      }
    }
  }

  overrideWidgetConfigDataFromQueryParamData(widgetConf: WidgetConfModel): WidgetConfModel {
    if (!widgetConf?.context) {
      widgetConf.context = new WidgetContext();
    }
    if (this.queryParamsData.widgetContext.worker_email) {
      LoggerService.warn('Overriding worker_email');
      widgetConf.context.worker_email = this.queryParamsData.widgetContext.worker_email;
    }
    if (this.queryParamsData.widgetContext?.store_zip) {
      LoggerService.warn('Overriding Store Zip code');
      widgetConf.context.store_zip = this.queryParamsData.widgetContext?.store_zip;
    }
    if (this.queryParamsData.widgetContext.store_id) {
      LoggerService.warn('Overriding store');
      widgetConf.context.store_id = this.queryParamsData.widgetContext.store_id;
    }
    if (this.queryParamsData.widgetContext?.appointment_service_ids?.length > 0) {
      LoggerService.warn('Overriding appointment services');
      widgetConf.context.appointment_service_ids = this.queryParamsData.widgetContext.appointment_service_ids;
    }
    if (this.queryParamsData.widgetContext.worker_id) {
      LoggerService.warn('Overriding worker Id');
      widgetConf.context.worker_id = this.queryParamsData.widgetContext.worker_id;
    }
    if (this.queryParamsData.widgetContext?.worker_ids?.length > 0) {
      LoggerService.warn('[worker_ids] Overriding widgetConf.context.worker_ids with:', this.queryParamsData.widgetContext.worker_ids);
      widgetConf.context.worker_ids = this.queryParamsData.widgetContext.worker_ids;
    }
    if (this.queryParamsData.widgetContext.worker_uuid) {
      LoggerService.warn('Overriding worker uuid');
      widgetConf.context.worker_uuid = this.queryParamsData.widgetContext.worker_uuid;
    }
    if (this.queryParamsData.widgetContext.store_id) {
      LoggerService.warn('Overriding store id');
      widgetConf.context.store_id = this.queryParamsData.widgetContext.store_id;
    }
    if (this.queryParamsData.widgetContext.lang) {
      LoggerService.warn('Overriding lang');
      widgetConf.context.lang = this.queryParamsData.widgetContext.lang;
    }
    if (this.queryParamsData.widgetContext.locale) {
      LoggerService.warn('Overriding locale');
      widgetConf.context.locale = this.queryParamsData.widgetContext.locale;
    }
    if (this.queryParamsData.widgetContext.date) {
      LoggerService.warn('Overriding date');
      widgetConf.context.date = this.queryParamsData.widgetContext.date;
    }
    if (this.queryParamsData.widgetContext.is_internal) {
      LoggerService.warn('Overriding is_internal');
      widgetConf.context.is_internal = this.queryParamsData.widgetContext.is_internal;
    }
    if (this.queryParamsData.widgetContext.booking_questions?.length > 0) {
      LoggerService.warn('Overriding booking questions');
      widgetConf.context.booking_questions = this.queryParamsData.widgetContext.booking_questions;
    }
    if (this.queryParamsData.widgetContext.hidden_booking_steps?.length > 0) {
      LoggerService.warn('Overriding hidden_booking_steps');
      widgetConf.context.hidden_booking_steps = this.queryParamsData.widgetContext.hidden_booking_steps;
    }
    if (this.queryParamsData.widgetContext.meeting_providers?.length > 0) {
      LoggerService.warn('Overriding meeting providers');
      widgetConf.context.meeting_providers = this.queryParamsData.widgetContext.meeting_providers;
    }
    if (this.queryParamsData.widgetContext.customer?.length > 0) {
      LoggerService.warn('Overriding Customer');
      widgetConf.context.customer = this.queryParamsData.widgetContext.customer;
    }
    if (this.queryParamsData.widgetContext.customer_required?.length > 0) {
      LoggerService.warn('Overriding Customer Required Fields');
      widgetConf.context.customer_required = this.queryParamsData.widgetContext.customer_required;
    }
    if (this.queryParamsData.widgetContext.customer_type_id) {
      LoggerService.warn('Overriding Customer type');
      widgetConf.context.customer_type_id = this.queryParamsData.widgetContext.customer_type_id;
    }
    if (this.queryParamsData.widgetContext.show_questions_before_booking_process) {
      LoggerService.warn('Overriding show questions before booking process');
      widgetConf.context.show_questions_before_booking_process = this.queryParamsData.widgetContext.show_questions_before_booking_process;
    }
    if (this.queryParamsData.widgetContext.steps?.length > 0) {
      LoggerService.warn('Overriding steps');
      widgetConf.context.steps = this.queryParamsData.widgetContext.steps;
    }
    if (this.queryParamsData.widgetContext.ask_for_customer_notification_preference) {
      LoggerService.warn('Overriding ask for customer notification preference');
      widgetConf.context.ask_for_customer_notification_preference = this.queryParamsData.widgetContext.ask_for_customer_notification_preference;
    }
    if (this.queryParamsData.widgetContext.show_meeting_type_picker) {
      LoggerService.warn('Overriding show meeting type picker');
      widgetConf.context.show_meeting_type_picker = this.queryParamsData.widgetContext.show_meeting_type_picker;
    }
    if (this.queryParamsData.widgetContext.enable_one_page_ui) {
      LoggerService.warn('Overriding Enable one page UI');
      widgetConf.context.enable_one_page_ui = this.queryParamsData.widgetContext.enable_one_page_ui;
    }
    if (this.queryParamsData.widgetContext.auto_step_forward_on_select) {
      LoggerService.warn('Overriding auto step forward on select');
      widgetConf.context.auto_step_forward_on_select = this.queryParamsData.widgetContext.auto_step_forward_on_select;
    }
    if (this.queryParamsData.widgetContext?.show_additional_services_provided_by_meeting_providers?.length) {
      LoggerService.warn('Overriding show Additional Services Provided By Meeting Providers');
      widgetConf.context.show_additional_services_provided_by_meeting_providers = this.queryParamsData.widgetContext.show_additional_services_provided_by_meeting_providers;
    }
    if (this.queryParamsData.widgetContext.ask_customer_city) {
      LoggerService.warn('Overriding ask customer city');
      widgetConf.context.ask_customer_city = this.queryParamsData.widgetContext.ask_customer_city;
    }
    if (this.queryParamsData.widgetContext.ask_customer_zip) {
      LoggerService.warn('Overriding ask customer zip');
      widgetConf.context.ask_customer_zip = this.queryParamsData.widgetContext.ask_customer_zip;
    }
    if (this.queryParamsData.widgetContext.utm_source) {
      LoggerService.warn('Overriding utm source');
      widgetConf.context.utm_source = this.queryParamsData.widgetContext.utm_source;
    }
    if (this.queryParamsData.widgetContext.utm_medium) {
      LoggerService.warn('Overriding utm medium');
      widgetConf.context.utm_medium = this.queryParamsData.widgetContext.utm_medium;
    }
    if (this.queryParamsData.widgetContext.utm_campaign) {
      LoggerService.warn('Overriding utm campaign');
      widgetConf.context.utm_campaign = this.queryParamsData.widgetContext.utm_campaign;
    }
    if (this.queryParamsData.widgetContext.utm_content) {
      LoggerService.warn('Overriding utm content');
      widgetConf.context.utm_content = this.queryParamsData.widgetContext.utm_content;
    }
    if (this.queryParamsData.widgetContext.utm_term) {
      LoggerService.warn('Overriding utm term');
      widgetConf.context.utm_term = this.queryParamsData.widgetContext.utm_term;
    }
    if (this.queryParamsData.widgetContext.utm_id) {
      LoggerService.warn('Overriding utm id');
      widgetConf.context.utm_id = this.queryParamsData.widgetContext.utm_id;
    }

    if (this.queryParamsData.widgetContext.auto_skip_steps !== undefined) {
      LoggerService.warn('Overriding auto_skip_steps');
      widgetConf.context.auto_skip_steps = ((!isNaN(this.queryParamsData.widgetContext.auto_skip_steps) && Number(this.queryParamsData.widgetContext.auto_skip_steps) === 1) ? 1 : 0);
    }

    if (this.queryParamsData.widgetContext.category?.length) {
      LoggerService.warn('Overriding category');
      widgetConf.context.category = this.queryParamsData.widgetContext.category;
    }

    if (this.queryParamsData.widgetContext?.saluation_display_type) {
      LoggerService.warn('Overriding saluation Display Type');
      widgetConf.context.saluation_display_type = this.queryParamsData.widgetContext.saluation_display_type === `radio` ? `radio` : `selection`;
    }

    if (this.queryParamsData.widgetContext?.filter_meetingtype_services_based_on_store_id) {
      LoggerService.warn('Overriding filter_meetingtype_services_based_on_store_id');
      widgetConf.context.filter_meetingtype_services_based_on_store_id = Number(this.queryParamsData.widgetContext.filter_meetingtype_services_based_on_store_id) === 1;
    }

    if (this.queryParamsData.widgetContext?.show_worker_preview_on_date_selection_step) {
      LoggerService.warn('Show worker preview on date selection step');
      widgetConf.context.show_worker_preview_on_date_selection_step = Number(this.queryParamsData.widgetContext.show_worker_preview_on_date_selection_step) === 1;
    }
    if (this.queryParamsData.widgetContext?.only_allow_store_selection_for_direct_sales) {
      LoggerService.warn('allow Store Selection For Direct Sales Only');
      widgetConf.context.only_allow_store_selection_for_direct_sales = Number(this.queryParamsData.widgetContext.only_allow_store_selection_for_direct_sales) === 1;
    }
    if (this.queryParamsData.widgetContext?.show_store_selection_for_customer_meeting_type) {
      LoggerService.warn('Show store selection for customer meeting type');
      widgetConf.context.show_store_selection_for_customer_meeting_type = Number(this.queryParamsData.widgetContext.show_store_selection_for_customer_meeting_type) === 1;
    }
    if (this.queryParamsData.widgetContext?.smart_widget_birthday_minimum_age_18) {
      widgetConf.context.smart_widget_birthday_minimum_age_18 = Number(this.queryParamsData.widgetContext.smart_widget_birthday_minimum_age_18) === 1;
    }
    if (this.queryParamsData.widgetContext?.coupon) {
      LoggerService.warn('Overriding coupon');
      widgetConf.context.coupon = this.queryParamsData.widgetContext.coupon;
    }

    LoggerService.log('Inside overrideWidgetConfigDataFromQueryParamData widgetConf ', widgetConf);
    return widgetConf;
  }

  setCustomFieldValuesFromIframePostMessage(customFieldValues: { id?: number | string, value?: any, ignore?: boolean, rawKey?: string }[]): void {
    // Mirror setCustomFieldValues (the querystring path): preserve the raw key the parent passed
    // (a numeric id OR a partner-unique string identifier) as rawKey so resolveCustomFieldIdentifiers
    // can translate an identifier to its numeric id once the questions load. A numeric key keeps its
    // legacy behaviour (Number(rawKey) === the id). Without this, an identifier-keyed context/SDK
    // prefill never resolves (the entry has no rawKey to match on).
    this.queryParamsData.customFieldValues = (customFieldValues ?? []).map(cf => {
      const rawKey = cf.rawKey ?? (cf.id != null ? String(cf.id) : undefined);
      // Keep id undefined (not NaN) for a malformed entry with no key, and default ignore to
      // false — full parity with the querystring parser's entry shape.
      return { ...cf, ignore: cf.ignore ?? false, rawKey, id: rawKey !== undefined ? Number(rawKey) : undefined };
    });
  }

  setCustomerDataFromIframePostMessage(customerConfData: CustomerDbModel): void {
    this.queryParamsData.customerConfData = this.widgetService.customerConfData = customerConfData;
  }

  setWidgetContextDataFromIframePostMessage(widgetContext: WidgetContext): void {
    this.queryParamsData.widgetContext = widgetContext;
  }

  setReferenceAppointmentUuidFromIframePostMessage(appointmentUuid: string): void {
    this.validateReferenceAppointmentAndSetInContext(appointmentUuid);
  }

  private setWidgetContextData(result: Params): void {
    LoggerService.log('Inside setWidgetContextData result ', result);
    if (result.worker_email) {
      this.queryParamsData.widgetContext.worker_email = result.worker_email;
    }
    if (result.worker_id) {
      this.queryParamsData.widgetContext.worker_id = Number(result.worker_id);
    }
    if (result.worker_ids) {
      this.queryParamsData.widgetContext.worker_ids = result.worker_ids.split(',').map(id => Number(id));
      LoggerService.log('[worker_ids] Parsed from query param:', this.queryParamsData.widgetContext.worker_ids);
    }
    if (result.worker_uuid) {
      this.queryParamsData.widgetContext.worker_uuid = result.worker_uuid;
    }

    if (result.hidden_booking_steps) {
      // Validate against the supported hideable steps (MEETING_TYPE/STORE/SERVICES/WORKERS) and drop anything
      // else, so a malformed or malicious query param can never break the flow — unknown or
      // unsupported values (incl. other booking views) are silently ignored.
      this.queryParamsData.widgetContext.hidden_booking_steps = result.hidden_booking_steps
        .split(',')
        .map((view: string) => view.trim())
        .filter((view: string) => HIDEABLE_BOOKING_STEPS.includes(view));
      LoggerService.log('[hidden_booking_steps] Parsed from query param:', this.queryParamsData.widgetContext.hidden_booking_steps);
    }

    if (result.auto_skip_steps !== undefined) {
      this.queryParamsData.widgetContext.auto_skip_steps = ((!isNaN(result.auto_skip_steps) && Number(result.auto_skip_steps) === 1) ? 1 : 0);
    }

    if (result.appointment_service_ids) {
      this.queryParamsData.widgetContext.appointment_service_ids = result.appointment_service_ids.split(',').map(id => Number(id));
    }
    if (result.store_zip) {
      this.queryParamsData.widgetContext.store_zip = result.store_zip;
    }
    if (result.store_id) {
      this.queryParamsData.widgetContext.store_id = Number(result.store_id);
    }
    if (result.lang) {
      this.queryParamsData.widgetContext.lang = result.lang;
    }
    if (result.locale) {
      this.queryParamsData.widgetContext.locale = result.locale;
    }

    const KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];

    const keyMappings = this.getKeyMappings(KEYS);

    keyMappings.forEach(({ underscoreKey, hyphenKey }) => {
      if (result[underscoreKey] || result[hyphenKey]) {
        this.queryParamsData.widgetContext[underscoreKey] = result[underscoreKey] || result[hyphenKey];
      }
    });

    if (result.date) {
      this.queryParamsData.widgetContext.date = result.date;
    }
    if (result.is_internal) {
      this.queryParamsData.widgetContext.is_internal = result.is_internal === 1;
    }
    if (result.booking_questions) {
      this.queryParamsData.widgetContext.booking_questions = result.booking_questions.split(',').map(id => Number(id));
    }
    if (result.meeting_providers) {
      this.queryParamsData.widgetContext.meeting_providers = result.meeting_providers.split(',').map(id => Number(id));
    }
    if (result.customer) {
      this.queryParamsData.widgetContext.customer = result.customer.split(',');
    }
    if (result.customer_required) {
      this.queryParamsData.widgetContext.customer_required = result.customer_required.split(',');
    }
    if (result.customer_type_id) {
      this.queryParamsData.widgetContext.customer_type_id = result.customer_type_id;
    }
    if (result.show_questions_before_booking_process) {
      this.queryParamsData.widgetContext.show_questions_before_booking_process = Number(result.show_questions_before_booking_process) === 1;
    }
    if (result.steps) {
      this.queryParamsData.widgetContext.steps = result.steps.split(',').map(id => Number(id));
    }
    if (result.ask_for_customer_notification_preference) {
      this.queryParamsData.widgetContext.ask_for_customer_notification_preference = Number(result.ask_for_customer_notification_preference) === 1;
    }
    if (result.show_meeting_type_picker) {
      this.queryParamsData.widgetContext.show_meeting_type_picker = Number(result.show_meeting_type_picker) === 1;
    }
    if (result.enable_one_page_ui) {
      this.queryParamsData.widgetContext.enable_one_page_ui = Number(result.enable_one_page_ui) === 1;
    }
    if (result.auto_step_forward_on_select) {
      this.queryParamsData.widgetContext.auto_step_forward_on_select = Number(result.auto_step_forward_on_select) === 1;
    }
    if (result.show_worker_preview_on_date_selection_step) {
      this.queryParamsData.widgetContext.show_worker_preview_on_date_selection_step = Number(result.show_worker_preview_on_date_selection_step) === 1;
    }
    if (result.only_allow_store_selection_for_direct_sales) {
      this.queryParamsData.widgetContext.only_allow_store_selection_for_direct_sales = Number(result.only_allow_store_selection_for_direct_sales) === 1;
    }
    if (result.show_store_selection_for_customer_meeting_type) {
      this.queryParamsData.widgetContext.show_store_selection_for_customer_meeting_type = Number(result.show_store_selection_for_customer_meeting_type) === 1;
    }
    if (result.smart_widget_birthday_minimum_age_18) {
      this.queryParamsData.widgetContext.smart_widget_birthday_minimum_age_18 = Number(result.smart_widget_birthday_minimum_age_18) === 1;
    }
    if (result.saluation_display_type) {
      this.queryParamsData.widgetContext.saluation_display_type = result.saluation_display_type === `radio` ? `radio` : `selection`;
    }
    if (result.show_additional_services_provided_by_meeting_providers) {
      this.queryParamsData.widgetContext.show_additional_services_provided_by_meeting_providers = result.show_additional_services_provided_by_meeting_providers.split(',').map(id => Number(id));
    }
    if (Number(result?.button_previous_step_1) === 0) {
      this.domManuplationService.setCustomNextPreviewButtonStyle('booking-questions-previous-btn');
    }
    if (Number(result?.button_next_step_1) === 0) {
      this.domManuplationService.setCustomNextPreviewButtonStyle('booking-questions-next-question-btn');
      this.domManuplationService.setCustomNextPreviewButtonStyle('booking-questions-finish-last-btn');
    }
    if (Number(result?.button_previous_step_2) === 0) {
      this.domManuplationService.setCustomNextPreviewButtonStyle('meeting-type-previous-btn');
    }
    if (Number(result?.button_next_step_2) === 0) {
      this.domManuplationService.setCustomNextPreviewButtonStyle('meeting-type-selection-next-btn');
    }
    if (Number(result?.button_previous_step_3) === 0) {
      this.domManuplationService.setCustomNextPreviewButtonStyle('store-selection-previous-btn');
    }
    if (Number(result?.button_next_step_3) === 0) {
      this.domManuplationService.setCustomNextPreviewButtonStyle('store-selection-next-btn');
    }
    if (Number(result?.button_previous_step_4) === 0) {
      this.domManuplationService.setCustomNextPreviewButtonStyle('services-selection-previous-btn');
    }
    if (Number(result?.button_next_step_4) === 0) {
      this.domManuplationService.setCustomNextPreviewButtonStyle('services-selection-next-btn');
    }
    if (Number(result?.button_previous_step_5) === 0) {
      this.domManuplationService.setCustomNextPreviewButtonStyle('workers-selection-previous-btn');
    }
    if (Number(result?.button_next_step_5) === 0) {
      this.domManuplationService.setCustomNextPreviewButtonStyle('workers-selection-next-btn');
    }
    if (Number(result?.button_previous_step_6) === 0) {
      this.domManuplationService.setCustomNextPreviewButtonStyle('slot-selection-previous-btn');
    }
    if (Number(result?.button_next_step_6) === 0) {
      this.domManuplationService.setCustomNextPreviewButtonStyle('slot-selection-next-btn');
    }
    if (Number(result?.button_previous_step_7) === 0) {
      this.domManuplationService.setCustomNextPreviewButtonStyle('booking-questions-previous-btn');
    }
    if (Number(result?.button_next_step_7) === 0) {
      this.domManuplationService.setCustomNextPreviewButtonStyle('booking-questions-next-question-btn');
      this.domManuplationService.setCustomNextPreviewButtonStyle('booking-questions-finish-last-btn');
    }
    if (Number(result?.button_previous_step_8) === 0) {
      this.domManuplationService.setCustomNextPreviewButtonStyle('personal-form-previous-btn');
    }
    if (Number(result?.button_next_step_8) === 0) {
      this.domManuplationService.setCustomNextPreviewButtonStyle('personal-form-finish-btn');
    }
    if (Number(result?.button_previous_step_9) === 0) {
      this.domManuplationService.setCustomNextPreviewButtonStyle('payment-method-previous-btn');
    }
    if (Number(result?.button_next_step_9) === 0) {
      this.domManuplationService.setCustomNextPreviewButtonStyle('payment-method-book-btn');
    }
    if ([1, 0].includes(Number(result?.ask_customer_city))) {
      this.queryParamsData.widgetContext.ask_customer_city = Number(result?.ask_customer_city) === 1 ? 1 : 0;
    }
    if ([1, 0].includes(Number(result?.ask_customer_zip))) {
      this.queryParamsData.widgetContext.ask_customer_zip = Number(result?.ask_customer_zip) === 1 ? 1 : 0;
    }
    if (result?.category) {
      try {
        this.queryParamsData.widgetContext.category = JSON.parse(result.category);
      } catch (error) {
        // Malformed `category` value (e.g. a hand-typed WC attribute that is not valid JSON)
        // must not abort widget init — log and skip instead of throwing.
        LoggerService.warn('[sw-route] Ignoring invalid category value: ' + String(error));
      }
    }
    if (result?.coupon) {
      this.queryParamsData.widgetContext.coupon = result.coupon;
    }
  }

  private setCustomerConfData(result: Params): void {
    result?.customer_uuid && (this.queryParamsData.customerConfData.uuid = this.widgetService.customerConfData.uuid = result.customer_uuid);
    result?.customer_email && (this.queryParamsData.customerConfData.email = this.widgetService.customerConfData.email = result.customer_email);
    result?.customer_prename && (this.queryParamsData.customerConfData.prename = this.widgetService.customerConfData.prename = result.customer_prename);
    result?.customer_lastname && (this.queryParamsData.customerConfData.lastname = this.widgetService.customerConfData.lastname = result.customer_lastname);
    result?.customer_mobile && (this.queryParamsData.customerConfData.mobile = this.widgetService.customerConfData.mobile = result.customer_mobile);
    result?.customer_phone && (this.queryParamsData.customerConfData.phone = this.widgetService.customerConfData.phone = result.customer_phone);
    result?.customer_street && (this.queryParamsData.customerConfData.street = this.widgetService.customerConfData.street = result.customer_street);
    result?.customer_zip && (this.queryParamsData.customerConfData.zip = this.widgetService.customerConfData.zip = result.customer_zip);
    result?.customer_city && (this.queryParamsData.customerConfData.city = this.widgetService.customerConfData.city = result.customer_city);
    result?.customer_salutation && (this.queryParamsData.customerConfData.salutation = this.widgetService.customerConfData.salutation = result.customer_salutation);
    result?.customer_birthday && (this.queryParamsData.customerConfData.birthday = this.widgetService.customerConfData.birthday = result.customer_birthday);
    result?.customer_notification_preference && (this.queryParamsData.customerConfData.notification_preference = this.widgetService.customerConfData.notification_preference = result.customer_notification_preference);
    result?.comment && (this.queryParamsData.customerConfData.comment = this.widgetService.customerConfData.comment = result.comment);
    result?.meeting_type_id && (this.queryParamsData.customerConfData.meeting_type_id = this.widgetService.customerConfData.meeting_type_id = Number(result.meeting_type_id));
    result?.country && (this.queryParamsData.customerConfData.country = this.widgetService.customerConfData.country = Number(result.country));
    result?.title && (this.queryParamsData.customerConfData.title = this.widgetService.customerConfData.title = Number(result.title));
    result?.company && (this.queryParamsData.customerConfData.company = this.widgetService.customerConfData.company = result.company);
    result?.policy && (this.queryParamsData.customerConfData.policy = this.widgetService.customerConfData.policy = (result.policy).toLowerCase() === 'true' ? true : false);
  }

  private setCustomFieldValues(result: Params): void {
    const paramKeys: string[] = Object.keys(result);
    const customFieldValues: { id?: number, value?: any, ignore?: boolean, rawKey?: string }[] = [];
    if (paramKeys?.length > 0) {
      const customFieldKeys: string[] = paramKeys.filter((item: string) => {
        return item.indexOf('custom_field') > -1;
      });
      for (const customFieldKey of customFieldKeys) {
        const customFieldValue: { id?: number, value?: any, ignore?: boolean, rawKey?: string } = {};
        // Preserve the raw URL key (may be a string identifier) so it can be resolved
        // to a numeric id once the questions load; default id stays the legacy numeric id.
        customFieldValue.rawKey = this.helperService.findStringBetween(customFieldKey, '[', ']');
        customFieldValue.id = Number(customFieldValue.rawKey);
        customFieldValue.value = result[customFieldKey];
        customFieldValue.ignore = false;
        customFieldValues.push(customFieldValue);
      }
    }
    LoggerService.log('Inside Route Service final customFieldValues ', customFieldValues);
    this.queryParamsData.customFieldValues = customFieldValues;
  }

  isAutoSkipStepsEnabled(): boolean {
    return (
      Number(this.widgetService.widgetConf.context.auto_skip_steps) === 1 ||
      this.widgetService.widgetConf.context.auto_skip_steps === undefined
    );
  }

  validateReferenceAppointmentAndSetInContext(appointmentUuid: string): void {
    this.setReferenceAppointmentUuid = appointmentUuid;
    this.customEventService.queryStringAppointmentUuid.emit(appointmentUuid);
  }

  getKeyMappings(keys: string[]): { underscoreKey: string, hyphenKey: string }[] {
    return keys.map(key => {
      return {
        underscoreKey: key,
        hyphenKey: key.replace(/_/g, '-')
      };
    });
  }

  /**
   * WC mode: initialise widget from element attributes instead of URL query params.
   * Mirrors the queryParams subscribe body exactly so CSV/Number/JSON coercion is identical.
   * Called from SmartWidgetElementComponent.ngAfterViewInit() after the <app-root> view exists.
   */
  initFromElementAttributes(result: Params): void {
    this.extractStripeRedirectParams(result);

    if (result.partner_uuid && result.widget_uuid) {
      this.customEventService.setWidgetConfigurationFromQueryParamsEvent.emit({
        partner_uuid: result.partner_uuid,
        widget_uuid: result.widget_uuid,
      });
    }
    this.stripeRedirectionData = this.getStripeRedirectionData();
    this.refreshQueryParamPresent = result.refresh !== undefined;
    if (Number(result.refresh) === 1) {
      this.localStorageService.clearWidgetKeys();
    }

    if (result?.appointment_uuid) {
      this.validateReferenceAppointmentAndSetInContext(result.appointment_uuid);
    }

    this.setCustomerConfData(result);
    this.setWidgetContextData(result);
    this.setCustomFieldValues(result);
  }

  /**
   * Context-driven twin of the `refresh` query param. Called at config arrival
   * (AppComponent.updateWidgetConfiguration) because the context does not exist during the
   * route/attribute init where the param clear runs.
   *
   * Two properties make that placement safe; both must hold if it is ever moved:
   * 1. No booking state has been read — `<app-booking>` is template-gated on widgetConf.
   * 2. The Stripe 3DS payload was already read into memory during route init, and the restore
   *    consumes that copy, so clearing its storage key cannot strand a card-challenge return.
   *
   * Query param wins by PRESENCE; runs once per load.
   */
  applyContextRefresh(context: WidgetContext | undefined): void {
    if (this.contextRefreshApplied) {
      return;
    }
    // Armed on the FIRST call whether or not the param vetoes: presence is re-evaluated per
    // element mount and per queryParams emission, so latching only on the non-vetoed path would
    // let a later absence re-open the clear mid-funnel.
    this.contextRefreshApplied = true;

    if (this.refreshQueryParamPresent) {
      return;
    }

    if (Number(context?.refresh) === 1) {
      this.localStorageService.clearWidgetKeys();
    }
  }
}
